import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { TranslationService } from '@sdbn/common';
import { OptionsColumn, TransportOption, StructuredTransportOption, BookingOffsetType } from '../../transport-options.model';
import { TransportOptionsAdministrationContext } from '../../transport-options-administration.context';
import { TransportOptionEditorComponent, TransportOptionEditorDialogData } from '../transport-option-editor/transport-option-editor.component';
import { DialogService } from '@sdbn/common';

@Component({
  selector: 'app-transport-options-strcuture-editor',
  templateUrl: './transport-options-strcuture-editor.component.html',
  styleUrls: ['./transport-options-strcuture-editor.component.scss']
})
export class TransportOptionsStructureEditorComponent implements OnInit, OnDestroy {

  private eventSource = 'app-transport-options-strcuture-editor';

  context: TransportOptionsAdministrationContext;
  contextSubscription: Subscription;

  @Input() set optionsContext(optionsContext: TransportOptionsAdministrationContext) {
    this.context = optionsContext;

    if (this.contextSubscription != null) {
      this.contextSubscription.unsubscribe();
    }
    if (this.context != null) {
      this.prepareColumns();
      this.contextSubscription = this.context.contextChange$.subscribe(
        change => {
          if (change.source !== this.eventSource && change.key === 'options-changed') {
            this.prepareColumns();
          }
        }
      );
    }
  }

  @Input() set addOption(addOption: TransportOption) {
    this.draggedOption = new StructuredTransportOption(addOption);
    this.draggedOption.uuid = null;
  }

  dirty = false;

  newOptionId = -1;

  columns = new Array<OptionsColumn>();
  viewColumns = new Array<OptionsColumn>();

  /** Drag and Drop fields */
  draggedOption: StructuredTransportOption;
  draggedColumn: OptionsColumn;

  constructor(
    public translationService: TranslationService,
    private dialog: MatDialog,
    private dialogService: DialogService
  ) { }

  ngOnDestroy(): void {
    if (this.contextSubscription != null) {
      this.contextSubscription.unsubscribe();
    }
  }

  public ngOnInit() { }

  public onDragOptionStart(event: DragEvent, draggedOption: StructuredTransportOption) {
    event.dataTransfer.effectAllowed = "move";
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData("text/plain", "");

    this.draggedOption = draggedOption;
  }

  public onDragOptionEnd(event: DragEvent) {
    this.draggedOption = null;
  }

  public onDragColumnStart(event: DragEvent, draggedColumn: OptionsColumn) {
    event.dataTransfer.effectAllowed = "move";
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData("text/plain", "");

    this.draggedColumn = draggedColumn;
  }

  public onDragColumnEnd(event: DragEvent) {
    event.dataTransfer.effectAllowed = "move";
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData("text/plain", "");

    this.draggedColumn = null;
  }

  public onDragOverColumnDropzone(event: DragEvent) {
    if (this.draggedOption != null) {
      event.preventDefault();
      return;
    }
    if (this.draggedColumn != null) {
      event.preventDefault();
      return;
    }
  }

  public onDragOverColumnRowDropzone(event: DragEvent) {
    if (this.draggedOption != null) {
      event.preventDefault();
      return;
    }
  }

  public onDragOverChildRowDropzone(event: DragEvent) {
    if (this.draggedOption != null && (this.draggedOption.parentId === null || this.draggedOption.children.length === 0)) {
      event.preventDefault();
      return;
    }
  }

  public onDropOptionToFirstRowOfColum(event: DragEvent, destinationColumn: OptionsColumn) {
    // Hinzufügen einer Option
    if (this.draggedOption != null) {

      if (this.draggedOption.uuid == null) {
        const insertedOption = new StructuredTransportOption({ id: this.newOptionId-- });
        destinationColumn.options.splice(0, 0, insertedOption);
        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      } else { // Verschieben einer Option
        if (this.draggedOption.parentId == null) { // Parent Option
          this.dropParentOptionToFirstRowOfColumn(event, destinationColumn);
        } else { // Child Option
          this.dropChildOptionToFirstRowOfColumn(event, destinationColumn);
        }
      }
    }
  }

  public onDropOptionAsFirstChild(event: DragEvent, targetParentOption: StructuredTransportOption) {
    // Hinzufügen einer Option
    if (this.draggedOption != null) {

      if (this.draggedOption.uuid == null) {
        const insertedOption = new StructuredTransportOption({ id: this.newOptionId--, parentId: targetParentOption.id });
        targetParentOption.children.splice(0, 0, insertedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      } else { // Verschieben einer Option
        if (this.draggedOption.parentId == null) { // Parent Option
          this.dropParentOptionAsFirstChild(event, targetParentOption);
        } else { // Child Option
          this.dropChildOptionAsFirstChild(event, targetParentOption);
        }
      }
    }
  }

  private dropParentOptionAsFirstChild(event: DragEvent, targetParentOption: StructuredTransportOption) {
    if (this.draggedOption != null && this.draggedOption.parentId == null && this.draggedOption.children.length === 0) {

      const sourceColum = this.findColumnByOption(this.draggedOption);

      if (sourceColum != null) {
        const movedOptionIndex = sourceColum.options.findIndex(o => o.uuid === this.draggedOption.uuid);
        sourceColum.options.splice(movedOptionIndex, 1);
        this.draggedOption.parentId = targetParentOption.id;
        targetParentOption.children.splice(0, 0, this.draggedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      }
    }
  }

  private dropChildOptionAsFirstChild(event: DragEvent, targetParentOption: StructuredTransportOption) {
    if (this.draggedOption != null && this.draggedOption.parentId != null && this.draggedOption.children.length === 0) {

      for (const column of this.columns) {
        const parentId = this.draggedOption.parentId;
        const parentOption = column.options.find(o => o.id === parentId);
        if (parentOption != null) {

          // Option aus vorherigem Parent entfernen
          const movedOption = parentOption.children.find(o => o.uuid === this.draggedOption.uuid);
          parentOption.children.splice(parentOption.children.indexOf(movedOption), 1);

          // Parent Id auf neuen Parent setzen
          movedOption.parentId = targetParentOption.id;
          targetParentOption.children.splice(0, 0, this.draggedOption);

          this.refreshColumnModel();
          this.updateOptionsInContext();
          this.context.dirty = true;
          //   this._template.validate();
          this.draggedOption = null;
          event.preventDefault();
          break;
        }
      }
    }
  }

  public onDropOptionAfterChild(event: DragEvent, targetParentOption: StructuredTransportOption, targetChildOption: StructuredTransportOption) {
    // Hinzufügen einer Option
    if (this.draggedOption != null) {

      if (this.draggedOption.uuid == null) {
        const insertedOption = new StructuredTransportOption({ id: this.newOptionId--, parentId: targetParentOption.id });
        targetParentOption.children.splice(targetChildOption.rowIndex + 1, 0, insertedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      } else { // Verschieben einer Option
        if (this.draggedOption.parentId == null) { // Parent Option
          this.dropParentOptionAfterChildOption(event, targetParentOption, targetChildOption);
        } else { // Child Option
          this.dropChildOptionAfterChildOption(event, targetParentOption, targetChildOption);
        }
      }
    }
  }

  private dropParentOptionAfterChildOption(event: DragEvent, targetParentOption: StructuredTransportOption, targetChildOption: StructuredTransportOption) {
    if (this.draggedOption != null && this.draggedOption.parentId == null && this.draggedOption.children.length === 0) {
      const sourceColum = this.findColumnByOption(this.draggedOption);

      if (sourceColum != null) {
        const movedOptionIndex = sourceColum.options.findIndex(o => o.uuid === this.draggedOption.uuid);
        sourceColum.options.splice(movedOptionIndex, 1);
        this.draggedOption.parentId = targetParentOption.id;
        targetParentOption.children.splice(targetChildOption.rowIndex + 1, 0, this.draggedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      }
    }
  }

  private dropChildOptionAfterChildOption(event: DragEvent, targetParentOption: StructuredTransportOption, targetChildOption: StructuredTransportOption) {
    if (this.draggedOption != null && this.draggedOption.parentId != null && this.draggedOption.children.length === 0) {

      for (const column of this.columns) {
        const parentId = this.draggedOption.parentId;
        const parentOption = column.options.find(o => o.id === parentId);
        if (parentOption != null) {

          if (parentOption.uuid !== targetParentOption.uuid) { // Zwischen zwei Parent Optionen verschieben
            // Option aus vorherigem Parent entfernen
            const movedOption = parentOption.children.find(o => o.uuid === this.draggedOption.uuid);
            parentOption.children.splice(parentOption.children.indexOf(movedOption), 1);

            // Parent Id auf neuen Parent setzen
            movedOption.parentId = targetParentOption.id;
            targetParentOption.children.splice(targetChildOption.rowIndex + 1, 0, this.draggedOption);
          } else if (targetChildOption.uuid !== this.draggedOption.uuid) {
            if (this.draggedOption.rowIndex > targetChildOption.rowIndex) {
              moveItemInArray(targetParentOption.children, this.draggedOption.rowIndex, targetChildOption.rowIndex + 1);
            } else {
              moveItemInArray(targetParentOption.children, this.draggedOption.rowIndex, targetChildOption.rowIndex);
            }
          }

          this.refreshColumnModel();
          this.updateOptionsInContext();
          this.context.dirty = true;
          //   this._template.validate();
          this.draggedOption = null;
          event.preventDefault();
          break;
        }
      }
    }
  }

  private dropParentOptionToFirstRowOfColumn(event: DragEvent, destinationColumn: OptionsColumn) {
    // Verschobene Option finden
    let movedOption: StructuredTransportOption = null;
    // Source Column finden
    let sourceColumn: OptionsColumn = null;
    // Index der Option in Source column
    let sourceOptionIndex = null;

    for (const column of this.columns) {
      const indexOfMovedOption = column.options.findIndex(o => o.uuid === this.draggedOption.uuid);
      if (indexOfMovedOption !== -1) {
        sourceOptionIndex = indexOfMovedOption;
        movedOption = column.options[indexOfMovedOption];
        sourceColumn = column;
        break;
      }
    }

    if (movedOption != null && sourceColumn != null) {
      if (destinationColumn.columnIndex === sourceColumn.columnIndex) { // Innerhalb derselben Spalte verschieben
        const fromIndex = destinationColumn.options.indexOf(movedOption);
        moveItemInArray(destinationColumn.options, fromIndex, 0);
        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      } else { // Zwischen Spalten verschieben

        // Option aus source column entfernen
        sourceColumn.options.splice(sourceOptionIndex, 1);

        if (sourceColumn.options.length === 0) {
          this.columns.splice(this.columns.indexOf(sourceColumn), 1);
        }

        // Option in neue Spalte einfügen
        destinationColumn.options.splice(0, 0, movedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      }
    }
  }

  private dropChildOptionToFirstRowOfColumn(event: DragEvent, destinationColumn: OptionsColumn) {
    if (this.draggedOption != null && this.draggedOption.parentId != null) {

      for (const column of this.columns) {
        const parentId = this.draggedOption.parentId;
        const parentOption = column.options.find(o => o.id === parentId);
        if (parentOption != null) {

          // Option aus vorherigem Parent entfernen
          const movedOption = parentOption.children.find(o => o.uuid === this.draggedOption.uuid);
          parentOption.children.splice(parentOption.children.indexOf(movedOption), 1);
          // Parent Id Entfernen
          movedOption.parentId = null;

          // Option in neue Spalte einfügen
          destinationColumn.options.splice(0, 0, movedOption);

          this.refreshColumnModel();
          this.updateOptionsInContext();
          this.context.dirty = true;
          //   this._template.validate();
          this.draggedOption = null;
          event.preventDefault();
          break;
        }
      }
    }
  }

  public onDropOptionToRowOfColumn(event: DragEvent, destinationColumn: OptionsColumn, destinationRowIndex: number) {

    // Hinzufügen einer Option
    if (this.draggedOption != null) {

      if (this.draggedOption.uuid == null) {
        const insertedOption = new StructuredTransportOption({
          id: this.newOptionId--,
          bookingOffsetType: BookingOffsetType.NONE
        }
        );

        destinationColumn.options.splice(destinationRowIndex + 1, 0, insertedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      } else { // Verschieben einer Option
        if (this.draggedOption.parentId == null) {
          this.dropParentOptionToRowOfColumn(event, destinationColumn, destinationRowIndex);
        } else {
          this.dropChildOptionToRowOfColumn(event, destinationColumn, destinationRowIndex);
        }
      }
    }
  }

  private dropParentOptionToRowOfColumn(event: DragEvent, destinationColumn: OptionsColumn, destinationRowIndex: number) {
    // Verschobene Option finden
    let movedOption: StructuredTransportOption = null;
    // Source Column finden
    let sourceColumn: OptionsColumn = null;
    // Index der Option in Source column
    let sourceOptionIndex = null;

    for (const column of this.columns) {
      const indexOfMovedOption = column.options.findIndex(o => o.uuid === this.draggedOption.uuid);
      if (indexOfMovedOption !== -1) {
        sourceOptionIndex = indexOfMovedOption;
        movedOption = column.options[indexOfMovedOption];
        sourceColumn = column;
        break;
      }
    }

    if (movedOption != null && sourceColumn != null) {
      if (destinationColumn.columnIndex === sourceColumn.columnIndex) { // Innerhalb derselben Spalte verschieben

        // const fromIndex = destinationColumn.options.indexOf(movedOption);
        const fromIndex = destinationColumn.options.indexOf(movedOption);

        if (fromIndex !== destinationRowIndex) {
          if (fromIndex > destinationRowIndex) {
            if (fromIndex - 1 !== destinationRowIndex) {
              moveItemInArray(destinationColumn.options, fromIndex, destinationRowIndex + 1);
            }
          } else if (fromIndex !== destinationRowIndex) {
            moveItemInArray(destinationColumn.options, fromIndex, destinationRowIndex);
          }

          this.refreshColumnModel();
          this.updateOptionsInContext();
          this.context.dirty = true;
          //   this._template.validate();
          this.draggedOption = null;
          event.preventDefault();
        }
      } else { // Zwischen Spalten verschieben

        // Option aus source column entfernen
        sourceColumn.options.splice(sourceOptionIndex, 1);
        // this.updateOptionsIndex(sourceColumn);

        if (sourceColumn.options.length === 0) {
          this.columns.splice(this.columns.indexOf(sourceColumn), 1);
        }

        // Option in neue Spalte einfügen
        destinationColumn.options.splice(destinationRowIndex + 1, 0, movedOption);

        this.refreshColumnModel();
        this.updateOptionsInContext();
        this.context.dirty = true;
        //   this._template.validate();
        this.draggedOption = null;
        event.preventDefault();
      }
    }
  }

  private dropChildOptionToRowOfColumn(event: DragEvent, destinationColumn: OptionsColumn, destinationRowIndex: number) {
    if (this.draggedOption != null && this.draggedOption.parentId != null) {

      for (const column of this.columns) {
        const parentId = this.draggedOption.parentId;
        const parentOption = column.options.find(o => o.id === parentId);
        if (parentOption != null) {

          // Option aus vorherigem Parent entfernen
          const movedOption = parentOption.children.find(o => o.uuid === this.draggedOption.uuid);
          parentOption.children.splice(parentOption.children.indexOf(movedOption), 1);
          // Parent Id Entfernen
          movedOption.parentId = null;

          // Option in neue Spalte einfügen
          destinationColumn.options.splice(destinationRowIndex + 1, 0, movedOption);

          this.refreshColumnModel();
          this.updateOptionsInContext();
          this.context.dirty = true;
          //   this._template.validate();
          this.draggedOption = null;
          event.preventDefault();
          break;
        }
      }
    }
  }

  public onDropToColumnDropzone(event: DragEvent, destinationColumn: OptionsColumn) {
    // drop to first column
    if (this.draggedOption != null) {

      if (this.draggedOption.parentId == null) { // Parent Option aus Column entfernen
        const sourceColumn = this.findColumnByOption(this.draggedOption);
        if (sourceColumn != null) {
          sourceColumn.options.splice(sourceColumn.options.indexOf(this.draggedOption), 1);
        }
      } else { // Child Option aus Parent entfernen
        const parentOption = this.findParentOption(this.draggedOption);
        if (parentOption != null) {
          parentOption.children.splice(parentOption.children.indexOf(this.draggedOption), 1);
        }
        this.draggedOption.parentId = null;
      }

      const insertedColumn = new OptionsColumn();

      if (this.draggedOption.uuid == null) {
        const insertedOption = new StructuredTransportOption({ id: this.newOptionId--, parentId: null });
        insertedColumn.options.push(insertedOption);
      } else {
        insertedColumn.options.push(this.draggedOption);
      }

      if (destinationColumn != null) {
        this.columns.splice(destinationColumn.columnIndex + 1, 0, insertedColumn);
      } else {
        this.columns.splice(0, 0, insertedColumn);
      }

      this.refreshColumnModel();
      this.updateOptionsInContext();
      this.context.dirty = true;
      //   this._template.validate();
      this.draggedOption = null;
      event.preventDefault();
    } else if (this.draggedColumn != null) {
      if (destinationColumn != null) {
        if (destinationColumn.columnIndex !== this.draggedColumn.columnIndex) {
          if (destinationColumn.columnIndex > this.draggedColumn.columnIndex) {
            moveItemInArray(this.columns, this.draggedColumn.columnIndex, destinationColumn.columnIndex);
          } else {
            moveItemInArray(this.columns, this.draggedColumn.columnIndex, destinationColumn.columnIndex + 1);
          }
        }
      } else {
        moveItemInArray(this.columns, this.draggedColumn.columnIndex, 0);
      }
      this.refreshColumnModel();
      this.updateOptionsInContext();
      this.context.dirty = true;
      //   this._template.validate();
      this.draggedOption = null;
      event.preventDefault();
    }
  }


  private prepareColumns() {

    const options = new Array<StructuredTransportOption>();

    // Parent Options hinzufügen
    const parentOptionsById = new Map<number, StructuredTransportOption>();
    for (const option of this.context.options) {
      if (option.parentId == null) {
        const parentOption = new StructuredTransportOption(option);
        options.push(parentOption);
        parentOptionsById.set(parentOption.id, parentOption);
      }
    }

    // Child Options in Parents einfügen
    for (const option of this.context.options) {
      if (option.parentId != null) {
        const parentOption = parentOptionsById.get(option.parentId);
        if (parentOption != null) {
          parentOption.children.push(new StructuredTransportOption(option));
        }
      }
    }

    const optionsColumns = new Array<OptionsColumn>();

    const columnsByColumnIndex = new Map<number, OptionsColumn>();
    for (const o of options) {
      let column = columnsByColumnIndex.get(o.columnIndex);
      if (column == null) {
        column = new OptionsColumn();
        column.columnIndex = o.columnIndex;
        columnsByColumnIndex.set(o.columnIndex, column);
        optionsColumns.push(column);
      }
      column.options.push(o);
    }

    // sortieren
    optionsColumns.sort((a: OptionsColumn, b: OptionsColumn) => {
      return a.columnIndex - b.columnIndex;
    });
    optionsColumns.forEach(
      col => {
        col.options = col.options.sort((a: StructuredTransportOption, b: StructuredTransportOption) => {
          return a.rowIndex - b.rowIndex;
        });
        col.options.forEach(
          po => {
            po.children = po.children.sort((a: StructuredTransportOption, b: StructuredTransportOption) => {
              return a.rowIndex - b.rowIndex;
            });
          }
        );
      }
    );

    this.columns = optionsColumns;

    console.log('prepareColumns', this.columns);

    this.refreshColumnModel();
  }

  private refreshColumnModel() {
    this.columns = this.columns.filter(c => c.options.length > 0);

    // Spaltenindex normalisieren
    let columnIndex = 0;
    this.columns.forEach(col => col.columnIndex = columnIndex++);

    for (const column of this.columns) {
      this.updateOptionsIndex(column);
    }

    console.log('refreshColumnModel', this.columns);
  }

  private updateOptionsInContext() {

    const options = new Array<TransportOption>();
    this.columns.forEach(
      column => {
        column.options.forEach(
          option => {
            options.push(new TransportOption(option));
            option.children.forEach(
              child => {
                options.push(new TransportOption(child));
              }
            );
          }
        );
      }
    );
    this.context.setOptions(options, this.eventSource);
  }

  private updateOptionsIndex(col: OptionsColumn) {
    let index = 0;
    col.options.forEach(o => o.rowIndex = index++);
    col.options.forEach(o => {
      o.columnIndex = col.columnIndex;
      let childIndex = 0;
      o.children.forEach(co => co.rowIndex = childIndex++);
    });
    console.log('updateOptionsIndex', col);
  }

  private findColumnByOption(option: StructuredTransportOption): OptionsColumn {
    for (const column of this.columns) {
      if (column.options.find(o => o.uuid === option.uuid) != null) {
        return column;
      }
    }
    return null;
  }

  private findParentOption(option: StructuredTransportOption): StructuredTransportOption {
    for (const column of this.columns) {
      for (const o of column.options) {
        if (o.id === option.parentId) {
          return o;
        }
      }
    }
    return null;
  }

  public editTransportOption(option: StructuredTransportOption) {

    const data = new TransportOptionEditorDialogData();
    data.transportOption = new StructuredTransportOption(option);


    this.dialogService.openMaxHeight(TransportOptionEditorComponent, data).afterClosed().subscribe(
      editedOption => {
        if (editedOption != null) {
          option.merge(editedOption);
          this.updateOptionsInContext();
          this.context.dirty = true;
        }
      }
    );
  }

  public deleteTransportOption(option: StructuredTransportOption) {

    let messageKey = 'tmc-yard.service.admin.transport-option-administration.transport-option-structure-editor.delete.confirm-dialog.message';
    if (option.parentId == null && option.children.length > 0) {
      messageKey = 'tmc-yard.service.admin.transport-option-administration.transport-option-structure-editor.delete-parent-with-children.confirm-dialog.message';
    }


    this.dialogService.showConfirm(
      'tmc-yard.service.admin.transport-option-administration.transport-option-structure-editor.delete.confirm-dialog.title',
      messageKey
    ).afterClosed().subscribe(() => {

      if (option.parentId == null) {
        // Wenn Parent gelöscht wird, werden auch alle Children gelöscht
        option.children.forEach(c => {
          if (c.id > 0) {
            c.deleted = true;
            this.context.addDeletedOption(new TransportOption(c));
          }
        });
        // Parent aus Column entfernen
        const column = this.findColumnByOption(option);
        column.options.splice(column.options.indexOf(option), 1);
      } else {
        // Child aus Parent entfernen
        const parent = this.findParentOption(option);
        parent.children.splice(parent.children.indexOf(option), 1);
      }

      if (option.id > 0) {
        option.deleted = true;
        this.context.addDeletedOption(new TransportOption(option));
      }

      for (const c of this.context.constraints) {
        c.transportOptionIds.splice(c.transportOptionIds.indexOf(option.id), 1);
      }

      for (const c of this.context.deletedConstraints) {
        c.transportOptionIds.splice(c.transportOptionIds.indexOf(option.id), 1);
      }

      this.refreshColumnModel();
      this.updateOptionsInContext();
      this.context.dirty = true;
    });

  }
}
